/**
 * Who is actually broken when a dashboard widget reports an error.
 *
 * The host collects every `sym.reportError` line the runtime binder raises and used to summarise them all
 * the same way — "Some things on this page didn't load correctly", with a "Copy details" and an "Ask
 * Symphony to fix" hand-off. That sentence is a claim about US, and most of the time it is false: the
 * commonest failure by far is a CONNECTED service that is down or unreachable (a self-hosted MCP server, a
 * connector's upstream API), which the widget already renders its own designed state for ("Could not reach
 * Home Assistant"). Blaming the page for someone else's outage misleads the user, and the hand-off sends
 * the assistant off to "fix" widgets that are not broken.
 *
 * So classify first. There are FOUR classes, and only the last one is ours:
 *
 * - `notConnected` — the toolkit has no connected account on this metasite (Composio's 404 / code `1810`,
 *   slug `ActionExecute_ConnectedAccountNotFound`). Not an error at all: a setup step nobody has taken. It
 *   is the ONE class with a cure the user can apply themselves, so it gets a name and a Connect route —
 *   and, crucially, every widget waiting on the same toolkit collapses into ONE line. Three cards reading
 *   Gmail is three cards, but it is still one fact and one click.
 * - `rateLimited` — the source turned the read away for volume (429 + rate-limit wording, or the
 *   platform's own `[RATE LIMITED UPSTREAM: …]` marker). Transient by construction: it clears with time,
 *   the widget picks the numbers up on its next refresh, and there is nothing to repair.
 * - `outage` — an external service failing to answer.
 * - `defect` — anything else. Something Symphony could actually fix.
 *
 * ⚠️ BE CONSERVATIVE. A line we cannot confidently place is a `defect`: an extra banner costs a moment of
 * attention, while a real bug quietly re-labelled "the service is down" is a bug nobody ever fixes. That is
 * why an outage needs BOTH halves of the evidence — proof the call left our process for a named external
 * endpoint, AND a transport/upstream failure — rather than a bare "timed out" that any of our own code
 * could equally have produced.
 *
 * ⚠️ THE SAME CAUTION IS WHY `notConnected` IS PINNED TO COMPOSIO'S OWN NOT-CONNECTED EVIDENCE and nothing
 * looser. A widget that names a Composio tool slug that DOES NOT EXIST must keep failing as a defect: that
 * is the widget's own bug (`skills/widget-data.ts` — "a slug that doesn't exist fails at render time on the
 * user's screen"), the builder is told never to bury it, and re-labelling it "connect your app" would hide
 * a real defect behind a button that cannot fix it. Composio reports the two cases differently — an absent
 * account is `1810` / "No connected account found for user", an absent slug is `TOOL_NOT_FOUND` / "not
 * found" — and we match only the first. There is deliberately NO blanket "a connector failure is excused"
 * rule here.
 *
 * The two new patterns are deliberate mirrors of the harness's already-hardened
 * `tools/composio/composio-failure-classes.ts` (`not-connected` and `vendor-quota`), including its bounded
 * `1810` and `429` — an unbounded number matches the head of a longer one, and `429` unbounded once matched
 * the digits inside a Gmail draft id. Keep the two in step.
 *
 * Pure + import-free on purpose: it is exercised directly by jest, which cannot load the app's graph.
 */

/** Proof the failure happened on the far side of a call OUT of our process, at a named endpoint. */
const EXTERNAL_CALL = [
  // The tool relay's own shape, e.g. `Error calling ha_list_floors_areas on "http://host:8123": fetch failed`.
  /\bcalling\s+\S+\s+on\s+["'][^"']+["']/i,
  /\bmcp\s+server\b/i,
  /\bupstream\b/i,
  /\bserver seems unreachable\b/i,
];

/** Proof the far side never answered (or answered as a broken gateway). */
const TRANSPORT_FAILURE = [
  /\bfetch failed\b/i,
  /\bnetwork (?:error|request failed)\b/i,
  /\b(?:econnrefused|econnreset|etimedout|enotfound|eai_again|ehostunreach|enetunreach|epipe)\b/i,
  /\bconnection (?:refused|reset|closed)\b/i,
  /\bsocket hang up\b/i,
  /\bgetaddrinfo\b/i,
  /\b(?:unreachable|offline|not reachable)\b/i,
  /\btimed out\b/i,
  /\btimeout\b/i,
  /\b(?:502|503|504)\b/,
  /\bbad gateway\b/i,
  /\bservice unavailable\b/i,
  /\bgateway time-?out\b/i,
];

const matchesAny = (patterns: RegExp[], line: string) => patterns.some((p) => p.test(line));

/**
 * Composio's "this app is not connected on this account AT ALL" evidence, and nothing looser.
 *
 * Mirror of `composio-failure-classes.ts`'s `not-connected` pattern plus the slug the relay echoes.
 * `1810` is bounded (`\b`) on purpose: a code that merely STARTS 1810 is a different verdict about a
 * different failure. `CONNECTOR_DISCONNECTED` is the widget bridge's own code for the same verdict and
 * for a connection that needs reconnecting (`connectorDisconnectedBody` in the harness `tool-call.ts`),
 * which names its app as `for toolkit <slug>`.
 */
const NOT_CONNECTED = [
  /ActionExecute_ConnectedAccountNotFound/i,
  /No connected account found for user/i,
  /"code":\s*1810\b/i,
  /\bCONNECTOR_DISCONNECTED\b/i,
];

/**
 * The toolkit whose account is missing, as Composio names it: `for toolkit gmail` / `for toolkit 'gmail'`.
 * Anchored on the word `toolkit` so it can never pick up the neighbouring `under entity '<uuid>'` — an
 * internal id is exactly the kind of thing that must never reach a user.
 */
const TOOLKIT = /\bfor\s+toolkit\s+["'`]?([a-z0-9][a-z0-9_.-]*)/i;

/**
 * Refused for volume. Mirrors `composio-failure-classes.ts`'s `vendor-quota` and the platform's own
 * `RATE_LIMITED_DIRECTIVE` (`code-mode-execute.ts`), with ONE deliberate tightening: a bare `429` is NOT
 * enough here. The harness pattern picks a retry directive for a model mid-run, where being wrong costs a
 * wasted turn; this pattern SUPPRESSES a defect banner, where being wrong hides a bug from everyone. So we
 * require the line to actually say it was rate limited. A naked `429` stays a defect.
 */
const RATE_LIMITED = [/\bRESOURCE_EXHAUSTED\b/i, /\brate[ -]?limit/i, /\btoo many requests\b/i];

/** True only when the line proves an external endpoint failed to answer. Everything else is ours. */
export function isServiceOutage(line: string): boolean {
  if (!line) return false;
  return matchesAny(EXTERNAL_CALL, line) && matchesAny(TRANSPORT_FAILURE, line);
}

/** True only on Composio's own not-connected evidence. A missing TOOL SLUG is not this — it is a defect. */
export function isNotConnected(line: string): boolean {
  if (!line) return false;
  return matchesAny(NOT_CONNECTED, line);
}

/** True only when the line says, in words, that the source refused the read for volume. */
export function isRateLimited(line: string): boolean {
  if (!line) return false;
  return matchesAny(RATE_LIMITED, line);
}

/**
 * The toolkit slug named by a not-connected line (`gmail`), or null when the line names none. Never
 * invents one — the caller falls back to unnamed copy rather than guessing which app to send the user to.
 */
export function notConnectedToolkit(line: string): string | null {
  const slug = TOOLKIT.exec(line || '')?.[1]?.toLowerCase().trim();
  return slug || null;
}

/**
 * A toolkit slug as a person writes it. Curated where the casing is a brand decision nobody can infer
 * (`GitHub`, `HubSpot`, `monday.com`), otherwise the slug's words title-cased — which is right far more
 * often than it is wrong, and is never an internal id. Mirrored 1:1 on iOS + Android.
 */
const TOOLKIT_LABELS: Record<string, string> = {
  github: 'GitHub',
  gmail: 'Gmail',
  googlecalendar: 'Google Calendar',
  googledocs: 'Google Docs',
  googledrive: 'Google Drive',
  googlesheets: 'Google Sheets',
  hubspot: 'HubSpot',
  linkedin: 'LinkedIn',
  monday: 'monday.com',
  one_drive: 'OneDrive',
  onedrive: 'OneDrive',
  outlook: 'Outlook',
  whatsapp: 'WhatsApp',
  youtube: 'YouTube',
};

export function toolkitDisplayName(toolkit: string): string {
  const slug = (toolkit || '').toLowerCase().trim();
  if (!slug) return '';
  const curated = TOOLKIT_LABELS[slug];
  if (curated) return curated;
  return slug
    .split(/[_\-.\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The service to name in the notice, read off the endpoint the relay reports (`on "<url>"`). A URL is
 * reduced to its host — the whole point is to say WHOSE outage this is, and a path or a port says nothing.
 * Returns null when the line names nothing we can show; the caller falls back to unnamed copy rather than
 * inventing a service.
 */
export function outageServiceName(line: string): string | null {
  const match = /\bon\s+["']([^"']+)["']/i.exec(line || '');
  const raw = match?.[1]?.trim();
  if (!raw) return null;
  const host = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? safeHost(raw) : raw.split('/')[0];
  const name = (host || '')
    .replace(/^www\./i, '')
    .replace(/:\d+$/, '')
    .trim();
  return name || null;
}

function safeHost(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

/** One line's verdict. Only `defect` is ours. */
export type DashboardErrorClass = 'notConnected' | 'rateLimited' | 'outage' | 'defect';

/**
 * The class of a single line. ORDER MATTERS: not-connected is tested first, because Composio's
 * not-connected body is a 404 that also says "not found", and the looser patterns below would claim it.
 * The harness classifier orders its list for exactly the same reason.
 */
export function dashboardErrorClass(line: string): DashboardErrorClass {
  if (!line || !line.trim()) return 'defect';
  if (isNotConnected(line)) return 'notConnected';
  if (isRateLimited(line)) return 'rateLimited';
  if (isServiceOutage(line)) return 'outage';
  return 'defect';
}

export interface ClassifiedDashboardErrors {
  /**
   * `defect` when at least one line is ours to fix; `outage` when nothing here is. Kept for the callers
   * that only need "is there anything to hand the assistant" — the banner itself renders per GROUP below,
   * because a page can be waiting on an unconnected Gmail AND holding a genuine defect at the same time.
   */
  kind: 'outage' | 'defect';
  /** The lines Symphony could actually fix. The copy/hand-off payload carries these and nothing else. */
  defects: string[];
  /**
   * True when at least one line is an external service failing to answer — NAMED OR NOT.
   *
   * Separate from `services` on purpose. An outage line whose endpoint we could not read names nothing, so
   * `services` stays empty while the outage is still real; a caller that keyed its outage stripe off
   * `services.length` would silently render no banner at all for it. That is exactly the bug this field
   * exists to prevent, and both native mirrors carry the same flag.
   */
  outage: boolean;
  /** The services named by the outage lines, deduped in arrival order. Empty when none could be named. */
  services: string[];
  /**
   * The toolkits with no connected account, deduped in arrival order — ONE entry however many widgets
   * reported it. Three cards failing on Gmail is one fact and one fix, and listing it three times triples
   * the alarm while hiding that a single click clears all of them.
   */
  disconnectedToolkits: string[];
  /** True when at least one line was refused for volume. Transient — it clears on its own. */
  rateLimited: boolean;
  /**
   * Every collected line, verbatim, in arrival order — INCLUDING the ones no banner shows. The on-screen
   * copy is human and never carries provider JSON, request ids or entity ids; this is the raw record for
   * the "Copy details" payload, so a report to a human still has the real thing in it.
   */
  all: string[];
}

export function classifyDashboardErrors(errors: readonly string[]): ClassifiedDashboardErrors {
  const lines = errors.filter((line) => !!line && !!line.trim());
  const defects: string[] = [];
  const services: string[] = [];
  const disconnectedToolkits: string[] = [];
  let rateLimited = false;
  let outage = false;

  lines.forEach((line) => {
    switch (dashboardErrorClass(line)) {
      case 'notConnected': {
        const toolkit = notConnectedToolkit(line);
        if (toolkit && !disconnectedToolkits.includes(toolkit)) disconnectedToolkits.push(toolkit);
        return;
      }
      case 'rateLimited':
        rateLimited = true;
        return;
      case 'outage': {
        outage = true;
        const name = outageServiceName(line);
        if (name && !services.includes(name)) services.push(name);
        return;
      }
      default:
        defects.push(line);
    }
  });

  return {
    kind: lines.length > 0 && defects.length === 0 ? 'outage' : 'defect',
    defects,
    outage,
    services,
    disconnectedToolkits,
    rateLimited,
    all: lines,
  };
}
